// i18n dictionary for Burj Al Qamar Trading F.Z.E.
// Two languages: English (en) + Arabic (ar). Arabic version renders RTL.

export const LANGS = ["en", "ar"];

export const DICT = {
  en: {
    // Nav
    nav_manifesto: "Manifesto",
    nav_catalogue: "Catalogue",
    nav_gallery: "Gallery",
    nav_sustainability: "Sustainability",
    nav_contact: "Contact",
    nav_cta_request: "Request Catalogue",
    nav_cta_download: "Download PDF",
    nav_menu: "Menu",
    nav_close: "Close",
    nav_locale_short: "EN",
    nav_locale_switch: "عربي",

    // Hero
    hero_meta_left: "Est. Ajman, UAE · Free Zone Enterprise",
    hero_meta_right: "Volume · 001  /  Foodservice Packaging",
    hero_title_1: "Packaging,",
    hero_title_2a: "quietly",
    hero_title_2b: "considered.",
    hero_body:
      "Burj Al Qamar Trading is an Ajman-based supplier of eco-conscious food packaging — paper cups, kraft food containers, takeaway boxes, aluminium, birch wood, hygiene barriers and engineered polymers — sourced, refined and delivered as a single, coherent system.",
    hero_stat_1_n: "05",
    hero_stat_1_l: "Material families",
    hero_stat_2_n: "120+",
    hero_stat_2_l: "SKUs on hand",
    hero_stat_3_n: "01",
    hero_stat_3_l: "Free Zone address · Ajman",
    hero_fig_caption: "Fig. 01 — Kraft Paper Cups",
    hero_scroll: "Scroll",

    // Marquee
    marquee_words: [
      "Kraft",
      "Aluminium",
      "Birch Wood",
      "Hygiene",
      "Recyclable Polymer",
      "Delivered Across UAE",
      "Free Zone Enterprise",
    ],

    // Manifesto
    manifesto_kicker: "§ Manifesto",
    manifesto_title:
      "A trading house that treats packaging as a discipline — not a commodity.",
    manifesto_chapters: [
      {
        n: "01",
        title: "Sourced with intention.",
        body:
          "Every fibre, foil and film is chosen for its origin, its footprint and its finish. We refuse the shortcut of anonymous supply.",
      },
      {
        n: "02",
        title: "Engineered to endure the meal.",
        body:
          "Packaging must hold, insulate, and disappear quietly. Nothing more. We tune each product for the exact life it must lead.",
      },
      {
        n: "03",
        title: "Designed to leave nothing behind.",
        body:
          "Kraft, birch, aluminium, recyclable resins. Materials chosen so the last stop is compost, recycling, or a second life — not landfill.",
      },
      {
        n: "04",
        title: "Delivered across the region.",
        body:
          "From our Free Zone base in the UAE, we ship in full container loads and small runs to restaurants, cloud kitchens, retailers and institutions.",
      },
    ],

    // Catalogue intro
    catalogue_kicker: "§ Catalogue",
    catalogue_title_1: "Five material families.",
    catalogue_title_2: "One coherent system.",
    catalogue_download: "Download the Catalogue (PDF)",
    catalogue_ref: "Ref.",
    catalogue_product: "Product",
    catalogue_products_count: (n) => `${n} products`,
    catalogue_request_quote: "Request quote",
    catalogue_plate: (n) => `Plate ${n}`,
    catalogue_studio_still: (t) => `${t} · Studio still`,
    chapter_kicker: (n) =>
      ["Chapter One", "Chapter Two", "Chapter Three", "Chapter Four", "Chapter Five"][
        parseInt(n, 10) - 1
      ] || `Chapter ${n}`,

    // Category names + subtitles + blurbs
    cat_paper_title: "Paper",
    cat_paper_sub: "Kraft. Coated. Considered.",
    cat_paper_blurb:
      "From ripple-wall coffee cups to SOS carry bags — a full architecture of paper packaging engineered for hot food, cold drinks and takeaway service.",
    cat_aluminium_title: "Aluminium",
    cat_aluminium_sub: "Heat. Hold. Deliver.",
    cat_aluminium_blurb:
      "Foil-wrapped, oven-ready. Round, rectangular and platter formats engineered for catering, cloud kitchens and airline service.",
    cat_wooden_title: "Wooden",
    cat_wooden_sub: "Grain. Grown. Gone.",
    cat_wooden_blurb:
      "Birch-wood cutlery, stirrers, chopsticks and toothpicks — biodegradable, splinter-free and finished with a paper-smooth feel.",
    cat_hygiene_title: "Hygiene",
    cat_hygiene_sub: "The invisible standard.",
    cat_hygiene_blurb:
      "Barrier essentials for foodservice, healthcare and clean production lines — gloves, masks, aprons, hair & beard covers.",
    cat_plastic_title: "Plastic",
    cat_plastic_sub: "Engineered for the long haul.",
    cat_plastic_blurb:
      "Microwave-safe blackbase, clear containers, cutlery, cling & stretch films, sofra, zipper bags and waste sacks — a full-spectrum system.",

    // Sustainability
    eco_kicker: "§ Ecology",
    eco_title:
      "Eco-friendly is not a label. It is a supply chain decision made every morning.",
    eco_pillar_label: "Pillar",
    eco_pillars: [
      {
        n: "i.",
        title: "Fibre from responsible forests.",
        body:
          "Kraft and birch are traced to well-managed sources — a small, boring detail that changes everything.",
      },
      {
        n: "ii.",
        title: "Aluminium that returns.",
        body:
          "Infinitely recyclable, chosen for the routes where heat and hold matter more than anything else.",
      },
      {
        n: "iii.",
        title: "Polymers, right-sized.",
        body:
          "Where plastic is the honest answer, we specify recyclable grades and clean forms — engineered, not indulgent.",
      },
    ],

    // Contact
    contact_kicker: "§ Correspondence",
    contact_title_1: "Write to",
    contact_title_2: "us.",
    contact_body:
      "Restaurants, cloud kitchens, retailers and institutions — send a brief note about what you need and we'll respond with a full specification and quotation.",
    contact_studio: "Studio",
    contact_studio_v: "Free Zone Enterprise · Ajman, UAE",
    contact_hours: "Hours",
    contact_hours_v: "Sun–Thu · 09:00–18:00 GST",
    contact_trade: "Trade",
    contact_trade_v: "Full container & mixed loads",
    contact_reach: "Reach",
    contact_reach_v: "GCC · Africa · Subcontinent",
    field_name: "01 · Name",
    field_name_ph: "Your full name",
    field_email: "02 · Email",
    field_email_ph: "you@company.com",
    field_company: "03 · Company",
    field_company_ph: "Optional",
    field_category: "04 · Category",
    field_category_ph: "Select a family",
    field_category_all: "Mixed / All",
    field_message: "05 · Message",
    field_message_ph: "Volumes, timelines, delivery city…",
    form_note_idle: "We reply within 24 hours",
    form_note_sent: "Received · thank you",
    form_submit_idle: "Send Inquiry",
    form_submit_sending: "Sending…",
    toast_success: "Inquiry received. We'll be in touch shortly.",
    toast_missing: "Name, email and message are required.",
    toast_error: "Something went wrong. Please try again.",

    // Products Gallery
    gallery_kicker: "§ Products Gallery",
    gallery_title_1: "The full range,",
    gallery_title_2: "photographed as one.",
    gallery_body:
      "Every plate below is our AI-composed studio still — a reference of the range on hand at our Ajman warehouse. Talk to us for exact dimensions, MOQs and lead times.",
    gallery_view_catalogue: "View full catalogue",

    // Footer
    footer_company_full: "Burj Al Qamar Trading F.Z.E.",
    footer_company_sub: "Free Zone Enterprise · Ajman, United Arab Emirates",
    footer_navigate: "Navigate",
    footer_families: "Families",
    footer_colophon: "Colophon",
    footer_colophon_body:
      "Set in Cormorant Garamond & Outfit. Composed on the shoreline of the Persian Gulf.",
    footer_copyright: (y) => `© ${y} Burj Al Qamar Trading F.Z.E.`,
    footer_volume: "Volume 001 · Foodservice Packaging",
    footer_back: "Return to top ↑",

    // WhatsApp quick-quote
    wa_fab: "Quick Quote",
    wa_kicker: "§ WhatsApp · Quick Quote",
    wa_title: "Message us on",
    wa_title_em: "WhatsApp",
    wa_body:
      "Tell us what you need. We'll respond on WhatsApp with a full spec and price — usually within a business hour.",
    wa_field_category: "Product family",
    wa_field_category_ph: "Select a family",
    wa_field_qty: "Estimated volume",
    wa_field_qty_ph: "e.g. 20,000 pcs / month",
    wa_field_note: "A brief note",
    wa_field_note_ph: "Delivery city, timeline, packaging preference…",
    wa_send: "Open WhatsApp",
    wa_close: "Close",
    wa_preview: "Message preview",
    wa_greeting: "Hello Burj Al Qamar — I would like a quote.",
    wa_line_family: "Family",
    wa_line_qty: "Volume",
    wa_line_note: "Note",
    wa_sent_from: "Sent from your website",
  },

  ar: {
    // Nav
    nav_manifesto: "البيان",
    nav_catalogue: "الكتالوج",
    nav_gallery: "المعرض",
    nav_sustainability: "الاستدامة",
    nav_contact: "تواصل",
    nav_cta_request: "اطلب الكتالوج",
    nav_cta_download: "تحميل PDF",
    nav_menu: "القائمة",
    nav_close: "إغلاق",
    nav_locale_short: "AR",
    nav_locale_switch: "EN",

    // Hero
    hero_meta_left: "تأسست في عجمان، الإمارات · منشأة منطقة حرة",
    hero_meta_right: "المجلد · ٠٠١  /  تغليف قطاع الأغذية",
    hero_title_1: ".تغليفٌ",
    hero_title_2a: "بهدوءٍ",
    hero_title_2b: ".مدروس",
    hero_body:
      "برج القمر للتجارة هي شركة مقرها عجمان بالإمارات متخصصة في توريد عبوات تغليف الطعام الصديقة للبيئة — أكواب ورقية، حاويات كرافت، علب طلبات خارجية، ألمنيوم، خشب البتولا، حواجز نظافة وبوليمرات هندسية — نستقيها وننقّيها ونوصلها ضمن منظومة واحدة متكاملة.",
    hero_stat_1_n: "٠٥",
    hero_stat_1_l: "عائلات مواد",
    hero_stat_2_n: "+١٢٠",
    hero_stat_2_l: "منتجات جاهزة",
    hero_stat_3_n: "٠١",
    hero_stat_3_l: "عنوان في المنطقة الحرة · عجمان",
    hero_fig_caption: "شكل ٠١ — أكواب ورق كرافت",
    hero_scroll: "مرّر",

    // Marquee
    marquee_words: [
      "كرافت",
      "ألمنيوم",
      "خشب البتولا",
      "نظافة",
      "بوليمر قابل للتدوير",
      "توصيل في الإمارات",
      "منشأة منطقة حرة",
    ],

    // Manifesto
    manifesto_kicker: "§ البيان",
    manifesto_title:
      "دار تجارية تعامل التغليف كانضباطٍ حرفي — لا كسلعةٍ عابرة.",
    manifesto_chapters: [
      {
        n: "٠١",
        title: "مصادر بنيّةٍ واضحة.",
        body:
          "كل ليفٍ ورقاقةٍ وفيلمٍ نختاره بناءً على منشئه وأثره ولمسته النهائية. نرفض اختصار المورد المجهول.",
      },
      {
        n: "٠٢",
        title: "مُصمَّم ليصمد طوال الوجبة.",
        body:
          "التغليف يجب أن يحمل ويعزل ثم ينسحب بهدوء. لا أكثر. نضبط كل منتجٍ لدورة الحياة التي سيعيشها بالضبط.",
      },
      {
        n: "٠٣",
        title: "مُصمَّم ألا يترك أثراً.",
        body:
          "كرافت، بتولا، ألمنيوم، وراتنجات قابلة للتدوير. مواد مختارة لتكون محطتها الأخيرة سماداً أو إعادة تدوير أو حياةً ثانية — لا مكبَّ نفايات.",
      },
      {
        n: "٠٤",
        title: "توصيلٌ في كل المنطقة.",
        body:
          "من مقرنا في المنطقة الحرة الإماراتية نشحن حاويات كاملة ودفعات صغيرة إلى المطاعم والمطابخ السحابية وتجّار التجزئة والمؤسسات.",
      },
    ],

    // Catalogue intro
    catalogue_kicker: "§ الكتالوج",
    catalogue_title_1: ".خمس عائلات مواد",
    catalogue_title_2: ".منظومة واحدة متسقة",
    catalogue_download: "تحميل الكتالوج (PDF)",
    catalogue_ref: "الرمز",
    catalogue_product: "المنتج",
    catalogue_products_count: (n) => `${n} منتج`,
    catalogue_request_quote: "اطلب عرض سعر",
    catalogue_plate: (n) => `لوحة ${n}`,
    catalogue_studio_still: (t) => `${t} · صورة استوديو`,
    chapter_kicker: (n) =>
      [
        "الفصل الأول",
        "الفصل الثاني",
        "الفصل الثالث",
        "الفصل الرابع",
        "الفصل الخامس",
      ][parseInt(n, 10) - 1] || `فصل ${n}`,

    cat_paper_title: "الورق",
    cat_paper_sub: ".كرافت. مطلي. مدروس",
    cat_paper_blurb:
      "من أكواب القهوة ذات الجدار المضلَّع إلى أكياس SOS — منظومة كاملة من عبوات التغليف الورقية مصممة للطعام الساخن والمشروبات الباردة وخدمات الوجبات الجاهزة.",
    cat_aluminium_title: "الألمنيوم",
    cat_aluminium_sub: ".الحرارة. الاحتفاظ. التوصيل",
    cat_aluminium_blurb:
      "ملفوف بالرقائق، جاهز للفرن. أشكال دائرية ومستطيلة وأطباق مصممة لخدمات التموين والمطابخ السحابية وخدمات الطيران.",
    cat_wooden_title: "الخشب",
    cat_wooden_sub: ".حبيبة. مزروع. زائل",
    cat_wooden_blurb:
      "أدوات مائدة وعصي تحريك وأعواد وأعواد أسنان من خشب البتولا — قابلة للتحلل، خالية من الشظايا، ذات لمسة نهائية ناعمة كالورق.",
    cat_hygiene_title: "النظافة",
    cat_hygiene_sub: ".المعيار غير المرئي",
    cat_hygiene_blurb:
      "الأساسيات الحاجزة لخدمات الأغذية والرعاية الصحية وخطوط الإنتاج النظيفة — قفازات، أقنعة، مآزر، أغطية شعر ولحية.",
    cat_plastic_title: "البلاستيك",
    cat_plastic_sub: ".مصمَّم للاستخدام الطويل",
    cat_plastic_blurb:
      "أوعية بلاك بيس آمنة للميكروويف، حاويات شفافة، أدوات مائدة، أفلام لصق وتغليف، سفرة، أكياس بسحّاب وأكياس نفايات — منظومة كاملة الطيف.",

    // Sustainability
    eco_kicker: "§ البيئة",
    eco_title:
      "الصداقة البيئية ليست ملصقاً. إنها قرار سلسلة توريد يُتَّخذ كل صباح.",
    eco_pillar_label: "ركيزة",
    eco_pillars: [
      {
        n: "i.",
        title: "ألياف من غابات مسؤولة.",
        body:
          "الكرافت والبتولا نتتبعهما إلى مصادر تُدار جيداً — تفصيل صغير مملّ يغيّر كل شيء.",
      },
      {
        n: "ii.",
        title: "ألمنيوم يعود.",
        body:
          "قابل للتدوير إلى ما لا نهاية، مختار للمسارات التي تكون فيها الحرارة والاحتفاظ الأهم على الإطلاق.",
      },
      {
        n: "iii.",
        title: "بوليمرات بالمقاس الصحيح.",
        body:
          "حيث يكون البلاستيك الجواب الأمين، نحدد درجات قابلة للتدوير وأشكالاً نظيفة — مهندسة لا مُبالَغ فيها.",
      },
    ],

    // Contact
    contact_kicker: "§ المراسلة",
    contact_title_1: "تواصل",
    contact_title_2: ".معنا",
    contact_body:
      "مطاعم، مطابخ سحابية، تجّار تجزئة ومؤسسات — أرسل لنا ملاحظة موجزة عمّا تحتاجه وسنرد بمواصفات كاملة وعرض سعر.",
    contact_studio: "المكتب",
    contact_studio_v: "منشأة منطقة حرة · عجمان، الإمارات",
    contact_hours: "الدوام",
    contact_hours_v: "الأحد–الخميس · ٠٩:٠٠–١٨:٠٠ توقيت الخليج",
    contact_trade: "التجارة",
    contact_trade_v: "حاويات كاملة وأحمال مختلطة",
    contact_reach: "النطاق",
    contact_reach_v: "الخليج · إفريقيا · شبه القارة",
    field_name: "٠١ · الاسم",
    field_name_ph: "اسمك الكامل",
    field_email: "٠٢ · البريد",
    field_email_ph: "you@company.com",
    field_company: "٠٣ · الشركة",
    field_company_ph: "اختياري",
    field_category: "٠٤ · الفئة",
    field_category_ph: "اختر عائلة",
    field_category_all: "مختلط / الكل",
    field_message: "٠٥ · الرسالة",
    field_message_ph: "الكميات، المواعيد، مدينة التسليم…",
    form_note_idle: "نرد خلال ٢٤ ساعة",
    form_note_sent: "تم الاستلام · شكراً لك",
    form_submit_idle: "إرسال الطلب",
    form_submit_sending: "…جارٍ الإرسال",
    toast_success: "تم استلام طلبك. سنتواصل معك قريباً.",
    toast_missing: "الاسم والبريد والرسالة حقول مطلوبة.",
    toast_error: "حدث خطأ. الرجاء المحاولة مجدداً.",

    // Products Gallery
    gallery_kicker: "§ معرض المنتجات",
    gallery_title_1: ".المجموعة الكاملة",
    gallery_title_2: ".مُصوَّرةً كوحدة واحدة",
    gallery_body:
      "كل لوحة أدناه هي طبيعة صامتة من استوديو تصويرنا — مرجعٌ للمجموعة المتوفرة في مستودعنا بعجمان. تواصل معنا للأبعاد الدقيقة، الحد الأدنى للطلب، ومواعيد التسليم.",
    gallery_view_catalogue: "استعرض الكتالوج الكامل",

    // Footer
    footer_company: "الشركة",
    footer_company_full: "برج القمر للتجارة ش.م.م.",
    footer_company_sub: "منشأة منطقة حرة · عجمان، الإمارات العربية المتحدة",
    footer_navigate: "التنقّل",
    footer_families: "العائلات",
    footer_colophon: "المرجع",
    footer_colophon_body:
      "منضود بحروف Cormorant Garamond و Outfit. مُؤلَّف على شاطئ الخليج العربي.",
    footer_copyright: (y) => `© ${y} برج القمر للتجارة ش.م.م.`,
    footer_volume: "المجلد ٠٠١ · تغليف قطاع الأغذية",
    footer_back: "العودة للأعلى ↑",

    // WhatsApp quick-quote
    wa_fab: "عرض سعر سريع",
    wa_kicker: "§ واتساب · عرض سعر سريع",
    wa_title: "راسلنا عبر",
    wa_title_em: "واتساب",
    wa_body:
      "أخبرنا بما تحتاجه. سنرد عبر واتساب بالمواصفات الكاملة والسعر — عادةً خلال ساعة عمل.",
    wa_field_category: "عائلة المنتج",
    wa_field_category_ph: "اختر عائلة",
    wa_field_qty: "الكمية التقديرية",
    wa_field_qty_ph: "مثلاً: ٢٠٬٠٠٠ قطعة / شهرياً",
    wa_field_note: "ملاحظة موجزة",
    wa_field_note_ph: "مدينة التسليم، الموعد، تفضيلات التغليف…",
    wa_send: "افتح واتساب",
    wa_close: "إغلاق",
    wa_preview: "معاينة الرسالة",
    wa_greeting: "مرحباً برج القمر — أرغب في الحصول على عرض سعر.",
    wa_line_family: "العائلة",
    wa_line_qty: "الكمية",
    wa_line_note: "ملاحظة",
    wa_sent_from: "مُرسَل من موقعكم",
  },
};

// Category id → localized strings
export function localizedCategory(dict, id) {
  const map = {
    paper: {
      title: dict.cat_paper_title,
      subtitle: dict.cat_paper_sub,
      blurb: dict.cat_paper_blurb,
    },
    aluminium: {
      title: dict.cat_aluminium_title,
      subtitle: dict.cat_aluminium_sub,
      blurb: dict.cat_aluminium_blurb,
    },
    wooden: {
      title: dict.cat_wooden_title,
      subtitle: dict.cat_wooden_sub,
      blurb: dict.cat_wooden_blurb,
    },
    hygiene: {
      title: dict.cat_hygiene_title,
      subtitle: dict.cat_hygiene_sub,
      blurb: dict.cat_hygiene_blurb,
    },
    plastic: {
      title: dict.cat_plastic_title,
      subtitle: dict.cat_plastic_sub,
      blurb: dict.cat_plastic_blurb,
    },
  };
  return map[id];
}

// Product name translation dictionary (English → Arabic).
// If a product has no entry, the English name is shown.
export const PRODUCT_AR = {
  "Kraft Paper Cups": "أكواب ورق كرافت",
  "Kraft Sauce Cups": "أكواب صلصة كرافت",
  "Kraft Containers": "حاويات كرافت",
  "Kraft Square Box": "علبة كرافت مربعة",
  "Kraft Salad Bowl": "طبق سلطة كرافت",
  "Kraft Sandwich Boxes": "علب سندويش كرافت",
  "Kraft Soup / Noodle Bowl": "طبق شوربة / نودلز كرافت",
  "Kraft Window Box": "علبة كرافت بنافذة",
  "Kraft Boat Trays": "صواني قارب كرافت",
  "Kraft Takeaway Box": "علبة كرافت للطلبات الخارجية",
  "Paper Carry Bags": "أكياس ورقية للحمل",
  "Paper SOS Bags": "أكياس ورقية SOS",
  "Paper Bags & Pouches": "أكياس وأظرف ورقية",
  "Paper Cups": "أكواب ورقية",

  "Aluminium Foil": "رقائق ألمنيوم",
  "Aluminium Round Containers": "حاويات ألمنيوم دائرية",
  "Aluminium Rectangular Containers": "حاويات ألمنيوم مستطيلة",
  "Aluminium Platter": "طبق ألمنيوم كبير",

  "Coffee Stirrer": "عصا تحريك القهوة",
  Chopstick: "عيدان طعام",
  Toothpick: "أعواد أسنان",
  "Wooden Cutleries": "أدوات مائدة خشبية",

  Gloves: "قفازات",
  "Plastic Apron": "مئزر بلاستيكي",
  "Face Masks": "أقنعة وجه",
  "Hair Net": "شبكة شعر",
  "Beard Cover": "غطاء لحية",

  "Blackbase HD Microwave Rectangle Container":
    "حاوية ميكروويف HD قاعدة سوداء مستطيلة",
  "Blackbase HD Microwave Round Container":
    "حاوية ميكروويف HD قاعدة سوداء دائرية",
  "Black Base Microwave Container": "حاوية ميكروويف بقاعدة سوداء",
  "Sushi Containers": "حاويات سوشي",
  "Clear Containers": "حاويات شفافة",
  "Microwave Rectangle Container": "حاوية ميكروويف مستطيلة",
  "Microwave Round Container": "حاوية ميكروويف دائرية",
  "Plastic Clear Juice Cups": "أكواب عصير بلاستيكية شفافة",
  "Plastic Cutleries": "أدوات مائدة بلاستيكية",
  "Cling Film": "فيلم لاصق",
  Sofra: "سفرة",
  "Stretch Wrap Film": "فيلم تغليف مطاطي",
  "Zipper Bags": "أكياس بسحّاب",
  "Dustbin Bags": "أكياس سلة مهملات",
  "Garbage Bags": "أكياس قمامة",
  "Plastic Carry Bags": "أكياس بلاستيكية للحمل",
};

export function translateProduct(dict, name, lang) {
  if (lang === "ar") return PRODUCT_AR[name] || name;
  return name;
}
